import Box from '@mui/material/Box'
import Fade from '@mui/material/Fade'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useEffect, useState } from 'react'

export interface RotatingMessagesProps {
  messages: string[]
  duration?: number
}

const FADE_DURATION = 450

export const RotatingMessages: React.FC<RotatingMessagesProps> = ({ duration = 6000, messages }) => {
  const [messageIndex, setMessageIndex] = useState(0)
  const [show, setShow] = useState(true)

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>

    const intervalId = setInterval(() => {
      setShow(false)

      timeoutId = setTimeout(() => {
        setMessageIndex((prev) => (prev + 1) % messages.length)
        setShow(true)
      }, FADE_DURATION)
    }, duration)

    return () => {
      clearInterval(intervalId)
      clearTimeout(timeoutId)
    }
  }, [messages.length, duration])

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: 'action.hover',
        borderRadius: 2.5,
        py: 1.5,
        px: 2,
        overflow: 'hidden',
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)',
          animation: 'shimmer 2.5s ease-in-out infinite',
        },
        '@keyframes shimmer': {
          '0%': { left: '-100%' },
          '100%': { left: '200%' },
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" spacing={2}>
        <Stack direction="row" alignItems="center" width="100%" spacing={1.5}>
          <Box
            sx={{
              width: 7,
              height: 7,
              flexShrink: 0,
              borderRadius: '50%',
              backgroundColor: 'warning.main',
              animation: 'blink 1.5s ease-in-out infinite',
              '@keyframes blink': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.3 },
              },
            }}
          />
          <Fade in={show} timeout={FADE_DURATION}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {messages[messageIndex]}
            </Typography>
          </Fade>
        </Stack>
        <AnimatedDots />
      </Stack>
    </Box>
  )
}

const AnimatedDots: React.FC = () => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      gap: 0.5,
      ml: 0.5,
      verticalAlign: 'middle',
      '& span': {
        width: 4,
        height: 4,
        borderRadius: '50%',
        backgroundColor: 'text.secondary',
        animation: 'dotBounce 1.4s ease-in-out infinite',
      },
      '& span:nth-of-type(2)': { animationDelay: '0.2s' },
      '& span:nth-of-type(3)': { animationDelay: '0.4s' },
      '@keyframes dotBounce': {
        '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
        '40%': { opacity: 1, transform: 'scale(1.2)' },
      },
    }}
  >
    <span />
    <span />
    <span />
  </Box>
)
